import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { GET_ROUTING_FORM } from "./graphql/operations";
import RoutingFormSubmit from "./RoutingFormSubmit";
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";

// Styled components
const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 900,
  margin: "0 auto",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  boxShadow: theme.shadows[2],
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "200px",
}));

/**
 * Public-facing component for embedding routing forms
 * Can be used in iframes or embedded directly in other pages
 */
function PublicRoutingForm() {
  const { formId } = useParams();
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch the form data
  const {
    loading,
    data,
    error: queryError,
  } = useQuery(GET_ROUTING_FORM, {
    variables: { id: formId },
    skip: !formId,
    onError: (err) => {
      console.error("GraphQL Error:", err);
      setError(err.message);
    },
  });

  // Update form data when query completes
  useEffect(() => {
    console.log("Query data received:", data);
    console.log("Query error:", queryError);

    if (data && data.getRoutingForm) {
      console.log("Routing form data:", data.getRoutingForm);

      // Check if form is active
      if (data.getRoutingForm.status !== "active") {
        setError("This form is no longer active.");
        return;
      }

      // Clear any previous errors
      setError(null);
      setFormData(data.getRoutingForm);
    } else if (data && !data.getRoutingForm) {
      console.warn("Query returned but no getRoutingForm data");
      setError("Form not found or has been removed.");
    }
  }, [data, queryError]);

  // Show loading state
  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </Container>
    );
  }

  // Show error state
  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // Show form not found - check both formData state AND query data
  if (!loading && !error && !formData && (!data || !data.getRoutingForm)) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          The requested form could not be found or has been removed.
        </Alert>
      </Container>
    );
  }

  // If we have query data but formData hasn't been set yet, show loading
  if (!formData && data && data.getRoutingForm) {
    return (
      <Container>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </Container>
    );
  }

  // Render the form submission component
  return (
    <Container>
      {/* Embed the form submission component */}
      <RoutingFormSubmit key={formId} />
    </Container>
  );
}

export default PublicRoutingForm;
